import React, { useEffect, useState, useCallback, Suspense, lazy } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useServices, Service, ServiceCategory } from "@/hooks/useServices";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import LoadingScreen from "@/components/LoadingScreen";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
const ServiceLocationMap = lazy(() => import("@/components/ServiceLocationMap").then(m => ({ default: m.ServiceLocationMap })));
import { ServiceImageUpload } from "@/components/services/ServiceImageUpload";
import { MapPin, Camera } from "lucide-react";
import { CollaboratorPicker, Collaborator } from "@/components/services/CollaboratorPicker";
import { useServiceCollaborators } from "@/hooks/useServiceCollaborators";

const formSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters").max(100, "Title must be less than 100 characters"),
  description: z.string().optional(),
  category_id: z.string().optional(),
  price: z.coerce.number().min(5, "Price must be at least $5"),
  delivery_method: z.enum(["virtual", "in-person", "both"]),
  service_location: z.object({
    latitude: z.number(),
    longitude: z.number(),
    radius: z.number().min(1, "Radius must be at least 1 km"),
  }).optional(),
  image_url: z.string().url().optional().or(z.literal("")),
}).refine((data) => {
  if (data.delivery_method === "in-person" || data.delivery_method === "both") {
    return data.service_location !== undefined;
  }
  return true;
}, {
  message: "Service location is required for in-person or hybrid delivery methods",
  path: ["service_location"]
});

type FormData = z.infer<typeof formSchema>;

const EditService = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, updateServiceAsync, isCategoriesLoading } = useServices();
  const [service, setService] = useState<Service | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceLocation, setServiceLocation] = useState<{latitude: number, longitude: number, radius: number} | null>(null);
  const [serviceImageUrl, setServiceImageUrl] = useState<string | null>(null);
  const { collaborators, setCollaborators, isLoading: isCollaboratorsLoading, saveCollaborators } = useServiceCollaborators(id);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      category_id: "",
      price: 0,
      delivery_method: "virtual" as const,
      image_url: "",
    },
  });

  const deliveryMethod = form.watch("delivery_method");
  const requiresLocation = deliveryMethod === "in-person" || deliveryMethod === "both";

  const handleLocationChange = useCallback((location: {latitude: number, longitude: number, radius: number}) => {
    console.log("Location changed:", location);
    setServiceLocation(location);
    form.setValue("service_location", location);
    form.clearErrors("service_location");
  }, [form]);

  const handleImageChange = useCallback((imageUrl: string | null) => {
    console.log("Image changed:", imageUrl);
    setServiceImageUrl(imageUrl);
    form.setValue("image_url", imageUrl || "");
  }, [form]);

  // Check initial conditions and handle redirects
  useEffect(() => {
    console.log("Initial checks - id:", id, "user:", user);
    
    if (!id) {
      console.log("No service ID found, redirecting to my-services");
      toast.error("Service ID not found");
      navigate("/my-services");
      return;
    }

    if (!user) {
      console.log("No user found, redirecting to auth");
      toast.error("You must be logged in to edit a service");
      navigate("/auth?mode=login");
      return;
    }
  }, [id, user, navigate]);

  // Fetch service data
  useEffect(() => {
    const fetchService = async () => {
      if (!id || !user) {
        console.log("Missing service ID or user, skipping fetch");
        return;
      }
      
      console.log("Fetching service:", id);
      setIsLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('services')
          .select(`
            *,
            service_categories:category_id (
              id,
              name,
              description
            )
          `)
          .eq('id', id)
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          console.error("Error fetching service:", error);
          toast.error("Failed to load service details");
          navigate("/my-services");
          return;
        }
        
        if (!data) {
          console.log("No service data found");
          toast.error("Service not found");
          navigate("/my-services");
          return;
        }
        
        // Parse service location
        let existingLocation = null;
        if (data.service_location) {
          try {
            existingLocation = typeof data.service_location === 'string' 
              ? JSON.parse(data.service_location) 
              : data.service_location;
            console.log("Parsed existing location:", existingLocation);
          } catch (e) {
            console.error("Error parsing service location:", e);
          }
        }
        
        let availabilitySchedule = null;
        if (data.availability_schedule) {
          try {
            availabilitySchedule = typeof data.availability_schedule === 'string'
              ? JSON.parse(data.availability_schedule)
              : data.availability_schedule;
          } catch (e) {
            console.error("Error parsing availability schedule:", e);
          }
        }
        
        const serviceData: Service = {
          ...data,
          pricing_type: "fixed" as const,
          delivery_method: data.delivery_method as "virtual" | "in-person" | "both" | null,
          service_location: existingLocation,
          availability_schedule: availabilitySchedule,
          image_url: data.image_url || null,
          service_categories: data.service_categories as ServiceCategory
        };
        
        console.log("Service data loaded successfully:", serviceData);
        setService(serviceData);
        setServiceLocation(existingLocation);
        setServiceImageUrl(serviceData.image_url || null);
        
        // Initialize form with service data
        const deliveryMethod = serviceData.delivery_method || "virtual";
        form.reset({
          title: serviceData.title,
          description: serviceData.description || "",
          category_id: serviceData.category_id || "",
          price: serviceData.price,
          delivery_method: deliveryMethod,
          service_location: existingLocation ?? undefined,
          image_url: serviceData.image_url || "",
        });
        
        console.log("Form initialized with service data");
        
      } catch (error) {
        console.error("Error in fetchService:", error);
        toast.error("An error occurred while loading the service");
        navigate("/my-services");
      } finally {
        console.log("Setting loading to false");
        setIsLoading(false);
      }
    };

    fetchService();
  }, [id, user, navigate, form]);

  const onSubmit = async (data: FormData) => {
    console.log("1. Handler Triggered (onSubmit)");
    console.log("Form Data:", data);
    console.log("Submit debug:", {
      id,
      isSubmitting,
      isDirty: form.formState.isDirty,
      requiresLocation,
      serviceLocation,
    });

    if (!id) return;

    if (requiresLocation && !serviceLocation) {
      toast.error(
        "Please set a service location on the map for in-person or hybrid services"
      );
      form.setError("service_location", {
        type: "required",
        message:
          "Service location is required for in-person or hybrid delivery methods",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Validate collaborator percentages
      const totalCollabPercentage = collaborators.reduce((sum, c) => sum + c.percentage, 0);
      if (totalCollabPercentage >= 100) {
        toast.error("Total collaborator percentages must be less than 100%");
        return;
      }

      const updateData = {
        ...data,
        service_location: requiresLocation ? serviceLocation : null,
        image_url: serviceImageUrl || null,
      };

      await updateServiceAsync({
        id: id,
        serviceData: updateData,
      });

      // Save collaborators
      try {
        await saveCollaborators(id, collaborators);
        console.log("Collaborators saved successfully");
      } catch (collabError) {
        console.error("Error saving collaborators:", collabError);
        toast.error("Service updated but failed to save collaborators");
      }

      toast.success("Service updated successfully");
      navigate("/my-services");
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error("Failed to update service");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading screen while fetching data
  if (isLoading) {
    return <LoadingScreen message="Loading service details..." />;
  }

  // Show loading screen if we don't have the service data yet (but we're not in loading state)
  if (!service) {
    return <LoadingScreen message="Loading service details..." />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/my-services")}
          className="mb-4"
        >
          ← Back to My Services
        </Button>
        <h1 className="text-3xl font-bold">Edit Service</h1>
        <p className="text-gray-600 mt-2">Update the details of your service</p>
      </div>
      
      <div className="max-w-3xl mx-auto">
        <Form {...form}>
          <form
            noValidate
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              console.log("2. Invalid handler triggered");
              console.log("Form validation errors:", errors);
              console.log("Form State Errors:", form.formState.errors);
            })}
            className="space-y-8"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Service title" {...field} />
                  </FormControl>
                  <FormDescription>
                    Create a clear and concise title for your service
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe what you offer in detail..." 
                      className="min-h-[150px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Explain what you're offering, your experience, and any specific details
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isCategoriesLoading ? (
                        <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                      ) : (
                        categories?.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Choose the category that best fits your service
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex flex-col md:flex-row gap-4 md:gap-8">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter your service price in USD
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
            </div>
            
            {/* Service Image Section - Make it more prominent */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 border-l-4 border-anyskill-blue">
              <h3 className="font-semibold text-xl mb-4 flex items-center">
                <Camera className="mr-2 text-anyskill-blue" />
                Service Image
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Add or update the image for your service to make it more appealing. A good image helps your service stand out to potential clients.
              </p>
              
              <ServiceImageUpload 
                onImageChange={handleImageChange}
                currentImageUrl={serviceImageUrl}
              />
            </div>
            
            <FormField
              control={form.control}
              name="delivery_method"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Delivery Method</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="virtual" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Virtual (online only)
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="in-person" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          In-Person (face to face)
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="both" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Both (virtual and in-person available)
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requiresLocation && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center">
                  <MapPin className="mr-2 text-anyskill-blue" />
                  Service Location
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Set your service area by clicking on the map to place a marker and adjusting the radius.
                </p>
                
                <Suspense fallback={null}>
                  <ServiceLocationMap 
                    onLocationChange={handleLocationChange}
                    initialLocation={serviceLocation}
                  />
                </Suspense>
                
                {form.formState.errors.service_location && (
                  <p className="text-sm font-medium text-destructive mt-2">
                    {form.formState.errors.service_location.message}
                  </p>
                )}
              </div>
            )}

            {/* Collaborators Section */}
            <CollaboratorPicker
              collaborators={collaborators}
              onCollaboratorsChange={setCollaborators}
            />
            
            <div className="flex justify-end gap-4 pt-4 relative z-10">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/my-services")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                onClick={() => {
                  console.log("0. Update button clicked", {
                    isSubmitting,
                    isDirty: form.formState.isDirty,
                  });
                }}
                className="bg-anyskill-purple hover:bg-anyskill-purple/90 active:scale-95 transition-transform"
              >
                {isSubmitting ? "Updating..." : "Update Service"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EditService;
